/**
 * One definition of "the draft order-set belonging to this ordering context".
 *
 * `getDraftOrderSetIdentity` returns the parts of that definition together so
 * they cannot disagree: the `matches` predicate, the `key` that dedupes
 * concurrent creates, and the `listParams` of the one list the draft appears
 * in. Those params live here too, so the hooks that fill each cache and the
 * lookup that reads it derive the same query key.
 */
import {
  DistributorOrderingCtx,
  SWEEPSTAKE_ORDERING_CTX_ID,
} from "@features/orderingCtx/OrderingCtx";
import { isActiveDraftOrderSet } from "@features/orders/helpers";
import { OrderSet, OrderSetType } from "@models/OrderSet";
import { RequestParams } from "@services/api";

/** Internal drafts, every type but pre-order. Never paginated. */
export const draftOrderSetsParams = (userId: string) => ({
  filter: {
    userIds: [userId],
    status: ["in-progress", "inactive"],
    type: "not-pre-order",
    isForDistributor: false,
    isCouponSweepstakes: false,
  },
  skipPagination: true,
});

/** Distributor and coupon-sweepstakes drafts. Never paginated. */
export const draftDistributorOrderSetsParams = (userId: string) => ({
  filter: {
    userIds: [userId],
    status: ["in-progress", "inactive"],
    isForDistributorOrCouponSweepstakes: true,
  },
  skipPagination: true,
});

/** What scopes a user's draft lists beyond the user themselves. */
export type DraftListScope = {
  userId: string;
  channel: string;
  territory?: string | null;
};

/** Internal pre-order order-sets, one per program and order window. */
export const preOrderOrderSetsParams = ({
  userId,
  channel,
  territory,
}: DraftListScope) => ({
  filter: {
    isPreOrderActive: true,
    type: "pre-order",
    userIds: [userId],
    ...(territory ? { territoryIds: [territory] } : { allRegions: true }),
    channel,
  },
});

export type DraftOrderSetContext = {
  orderType: OrderSetType;
  channel: string;
  /** Internal only; distributor drafts are scoped by the ordering context. */
  territory?: string | null;
  /** Present for distributor and sweepstakes catalogs. */
  orderingCtx?: DistributorOrderingCtx | null;
  /** Order calendar month, for pre-orders. */
  orderWindowId?: string;
  /** Part of a draft's identity for internal pre-orders only. */
  programId?: string | null;
};

export type DraftOrderSetIdentity = {
  key: string;
  matches: (orderSet: OrderSet) => boolean;
  /** Params of the single order-set list that would hold this draft. */
  listParams: (userId: string) => RequestParams;
};

const identity = ({
  scope,
  orderType,
  fields,
  matches,
  listParams,
}: {
  scope: string;
  orderType: OrderSetType;
  /**
   * Every field `matches` narrows by, `window` being the order window. The key
   * is derived from it, so a matcher cannot tell two contexts apart without
   * also giving them different keys, which is what stops one reusing the
   * other's draft.
   */
  fields: Record<string, string | null | undefined>;
  matches: (orderSet: OrderSet) => boolean;
  listParams: (userId: string) => RequestParams;
}): DraftOrderSetIdentity => ({
  key: [scope, orderType]
    .concat(Object.entries(fields).map(([k, v]) => `${k}=${v ?? ""}`))
    .join(":"),
  matches: (os) =>
    isActiveDraftOrderSet(os) &&
    os.type === orderType &&
    // A pre-order draft is per order window, so without one nothing matches.
    (orderType !== "pre-order" ||
      (!!fields.window && os.orderCalendarMonth?.id === fields.window)) &&
    matches(os),
  listParams,
});

export const getDraftOrderSetIdentity = ({
  orderType,
  channel,
  territory: rawTerritory,
  orderingCtx,
  orderWindowId: window,
  programId,
}: DraftOrderSetContext): DraftOrderSetIdentity => {
  // One spelling of "no territory", so the key and the matcher agree on it.
  const territory = rawTerritory || null;

  if (orderingCtx) {
    const { id: ctx, marketId, territoryId } = orderingCtx;
    if (ctx === SWEEPSTAKE_ORDERING_CTX_ID)
      // Sweepstakes orders are placed by internal users on behalf of everyone,
      // so they are all-regions and flagged as sweepstakes rather than for a
      // distributor, and channel is part of what identifies them.
      return identity({
        scope: "sweepstakes",
        orderType,
        fields: { ctx, channel, program: programId, window },
        matches: (os) =>
          os.isForDistributor === false &&
          os.isCouponSweepstakes === true &&
          os.channel === channel &&
          os.allRegions === true &&
          // This context orders the legacy way, where a pre-order order-set is
          // per program (see useDraftLegacyPreOrderOrderSet), so a draft only
          // matches the program being ordered.
          (orderType !== "pre-order" ||
            (!!programId && os.program?.id === programId)),
        listParams: draftDistributorOrderSetsParams,
      });
    // Program is absent from this identity on purpose: one of its pre-order
    // drafts holds items from several programs (see bulkAddVariantsWithPrograms).
    return identity({
      scope: "distributor",
      orderType,
      fields: { ctx, window },
      // An all-regions context has no market or territory of its own, so it
      // has to match on allRegions or it would claim a market's draft.
      matches: (os) =>
        os.isForDistributor === true &&
        (marketId ? os.market?.id === marketId : true) &&
        (territoryId ? os.territory?.id === territoryId : true) &&
        (marketId || territoryId ? true : os.allRegions === true),
      listParams: draftDistributorOrderSetsParams,
    });
  }

  if (orderType === "pre-order")
    return identity({
      scope: "internal-pre-order",
      orderType,
      fields: { channel, territory, program: programId, window },
      // Per program as well as window, so without a program there is nothing
      // specific enough to match (see useDraftPreOrderOrderSetQuery). The
      // distributor flags matter here because the pre-order list is not scoped
      // to internal order-sets: a sweepstakes draft is all-regions, so it
      // passes the same filters and would otherwise match on program alone.
      matches: (os) =>
        !!programId &&
        os.program?.id === programId &&
        !os.isForDistributor &&
        !os.isCouponSweepstakes,
      listParams: (userId) =>
        preOrderOrderSetsParams({ userId, channel, territory }),
    });

  return identity({
    scope: "internal",
    orderType,
    fields: { channel, territory },
    // isForDistributor / isCouponSweepstakes mirror the filters the API applies
    // to useDraftOrderSetsQuery, so reading a wider cache cannot widen the match.
    matches: (os) =>
      os.channel === channel &&
      (os.territory?.id ?? null) === territory &&
      !os.isForDistributor &&
      !os.isCouponSweepstakes,
    listParams: draftOrderSetsParams,
  });
};

export const findDraftOrderSet = (
  data: OrderSet[],
  context: DraftOrderSetContext
): OrderSet | undefined => data.find(getDraftOrderSetIdentity(context).matches);
